import { Menu } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';

import { PublicFooter } from './PublicFooter';

import { ScrollToTop } from '@/components/ScrollToTop';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { LOGO_J_PATH_32 } from '@/lib/logoMark';

const navLinks = [
  { label: 'Recursos', href: '/#recursos' },
  { label: 'Planos', href: '/#planos' },
  { label: 'Sobre', href: '/sobre' },
  { label: 'Contato', href: '/contato' },
];

function PublicLogo() {
  return (
    <Link to="/" className="flex items-center gap-3 no-underline">
      <svg
        viewBox="0 0 32 32"
        className="h-10 w-10 drop-shadow-[0_2px_8px_rgba(122,36,48,0.4)]"
        aria-hidden="true"
      >
        <rect width="32" height="32" rx="5.33" fill="var(--color-primary)" />
        <path d={LOGO_J_PATH_32} fill="#ffffff" />
      </svg>
      <span className="text-base font-bold tracking-tight">
        <span className="text-primary">JURIS</span>APP
      </span>
    </Link>
  );
}

/**
 * Chrome compartilhado das páginas públicas (header + footer) usando tokens de
 * tema — respeita light/dark, ao contrário do hardcode dark anterior.
 */
export function PublicLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    if (href.startsWith('/#')) {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href.substring(1));
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(href);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ScrollToTop />
      {/* Header */}
      <header className="fixed top-[var(--env-banner-h,0px)] z-50 w-full border-b border-border bg-background/92 backdrop-blur-md">
        <div className="container mx-auto flex h-14 max-w-screen-lg items-center justify-between px-4">
          <PublicLogo />

          {/* Desktop Navigation */}
          <div className="hidden items-center gap-8 md:flex">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className="cursor-pointer text-sm font-medium text-muted-foreground no-underline transition-colors hover:text-primary"
              >
                {link.label}
              </a>
            ))}
            <Button
              variant="unstyled"
              onClick={() => login()}
              className="ml-2 bg-gradient-to-br from-primary to-[var(--color-primary-dark)] px-4 py-2 text-white shadow hover:opacity-90"
            >
              Login
            </Button>
          </div>

          {/* Mobile menu button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="Abrir menu de navegação">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]">
              <div className="p-4">
                <PublicLogo />
              </div>
              <nav className="flex flex-col">
                {navLinks.map((link) => (
                  <Button
                    key={link.label}
                    variant="ghost"
                    onClick={() => handleNavClick(link.href)}
                    className="w-full justify-start rounded-none px-4 py-3 text-left text-sm font-normal text-muted-foreground"
                  >
                    {link.label}
                  </Button>
                ))}
                <Button
                  variant="ghost"
                  onClick={() => login()}
                  className="w-full justify-start rounded-none px-4 py-3 text-left text-sm font-normal text-primary"
                >
                  Login
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main id="main-content" tabIndex={-1}>
        {children}
      </main>

      <PublicFooter />
    </div>
  );
}

export default PublicLayout;
